import { supabase } from '../supabaseClient';
import { callAiBackend } from '../geminiService';
import { TUTORIAL_REGISTER } from '../../tutorial/register';
import type { AppView } from '../../types';
import type { TutorialKapitelId, TutorialRolle } from '../../tutorial/types';
import { istNochNichtVeroeffentlicht } from './tutorialService';
import {
    KEINE_HILFE_ANTWORT,
    KI_NICHT_ERREICHBAR,
    normalisiereAntwort,
    normalisiereFrage,
} from './hilfeChatLogic';
import type { HilfeAntwort, HilfeKapitelKontext } from './hilfeChatLogic';

/**
 * LERNZENTRUM — der Chat „Frag OS-IA" (Plan §1.8, Paket E).
 *
 * Die EINZIGE Stelle, an der der Chat mit der KI und mit `tutorial_fragen`
 * redet. Alles Rechnende (Kontextbau, Kappung, Antwortpruefung) steht in
 * `hilfeChatLogic.ts` und ist dort ohne Supabase geprueft — Tests laufen hier
 * ohne jsdom, also bleibt der Container duenn.
 *
 * ─── Was den Chat erdet ─────────────────────────────────────────────────────
 *
 * Der Kontext kommt AUS DEM CLIENT (Register im Bundle + freigegebene
 * Kapiteltexte), nicht aus dem Proxy. Der Proxy laeuft mit Service-Role; was
 * er selbst aus `tutorial_kapitel` holte, umginge die RLS und muesste die
 * Rollenabbildung ein zweites Mal nachbauen. Die Begruendung steht ausfuehrlich
 * am Fall `assistHelp` in `supabase/functions/gemini-proxy/index.ts`.
 *
 * ─── Warum das Speichern fail-open ist ──────────────────────────────────────
 *
 * `tutorial_fragen` entsteht mit Migration Nr. 100/101; bis dahin antwortet
 * PostgREST mit `42P01`/`PGRST205`. Eine fehlende Tabelle darf die ANTWORT
 * nicht kosten: der Nutzer hat seine Auskunft, die Zeile ist nur die
 * Lueckenliste fuer die Verwaltung. Sie fehlt dann eben.
 *
 * ─── Was NICHT gespeichert wird ─────────────────────────────────────────────
 *
 * Ein gescheiterter KI-Aufruf (Netz, Ratenbegrenzung, Ausfall) wird NICHT als
 * Frage abgelegt. Die Lueckenliste im Admin-Reiter zeigt Fragen OHNE passendes
 * Kapitel — also fehlenden Lernstoff. Eine Stoerung ist kein fehlender
 * Lernstoff, und wer sie dort einsortiert, laesst die Verwaltung Kapitel
 * schreiben, die es laengst gibt.
 */

const T_FRAGEN = 'tutorial_fragen';

/** Alle IDs, die es wirklich gibt. Dagegen wird jede Modellantwort gefiltert. */
const REGISTER_IDS: ReadonlySet<TutorialKapitelId> = new Set(TUTORIAL_REGISTER.map((k) => k.id));

export interface FrageEingabe {
    frage: string;
    /** Die Ansicht, aus der gefragt wurde. Landet in `tutorial_fragen.view`. */
    view: AppView;
    rollen: TutorialRolle[];
    /** Fertig gebauter Kontext aus `kontextFuerFrage`. */
    kapitel: HilfeKapitelKontext[];
}

export interface FrageErgebnis extends HilfeAntwort {
    ok: boolean;
    /** Id der Zeile in `tutorial_fragen` — Voraussetzung fuer `bewerteAntwort`. */
    frageId: string | null;
    /** Gesetzt, wenn `ok` falsch ist. Bereits verstaendlicher deutscher Text. */
    fehler?: string;
}

const aktuelleUserId = async (): Promise<string | null> => {
    try {
        const { data } = await supabase.auth.getUser();
        return data?.user?.id ?? null;
    } catch {
        return null;
    }
};

/**
 * Frage und Antwort ablegen. Gibt die Id der Zeile zurueck oder `null`.
 *
 * `null` ist ein voellig normaler Ausgang: Migration nicht gelaufen, nicht
 * angemeldet, RLS. Die Oberflaeche blendet dann nur die Daumen aus — die
 * Antwort steht trotzdem da.
 */
const merkeFrage = async (
    frage: string,
    view: AppView,
    antwort: string,
    kapitelIds: TutorialKapitelId[],
): Promise<string | null> => {
    const userId = await aktuelleUserId();
    if (!userId) return null;
    try {
        const { data, error } = await supabase
            .from(T_FRAGEN)
            .insert({
                user_id: userId,
                view,
                frage,
                antwort,
                kapitel_ids: kapitelIds,
            })
            .select('id')
            .single();
        if (error) throw error;
        const id = (data as { id?: unknown } | null)?.id;
        return typeof id === 'string' ? id : null;
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Frage nicht gemerkt', e);
        return null;
    }
};

/**
 * Eine Frage stellen.
 *
 * Wirft NIE. Der Chat steht neben einer Hilfe, die ohne ihn vollstaendig ist;
 * ein geworfener Fehler muesste in der Komponente gefangen werden, und wer ihn
 * vergisst, reisst die Ansicht mit. `ok: false` traegt denselben Text.
 *
 * Reihenfolge: erst antworten, dann merken. Scheitert das Merken, hat der
 * Nutzer seine Antwort trotzdem — nur ohne Daumen.
 */
export const frageStellen = async ({ frage, view, rollen, kapitel }: FrageEingabe): Promise<FrageErgebnis> => {
    const sauber = normalisiereFrage(frage);
    if (!sauber) {
        return { ok: false, antwort: '', kapitelIds: [], frageId: null, fehler: 'Bitte eine Frage eingeben.' };
    }

    let roh: unknown;
    try {
        roh = await callAiBackend('assistHelp', {
            frage: sauber,
            view,
            rollen,
            kapitelKontext: kapitel,
        });
    } catch (error) {
        // `callAiBackend` hat den Text bereits verstaendlich gemacht
        // (describeAiError) — er wird hier nur durchgereicht.
        return {
            ok: false,
            antwort: '',
            kapitelIds: [],
            frageId: null,
            fehler: (error as Error)?.message || KI_NICHT_ERREICHBAR,
        };
    }

    const geprueft = normalisiereAntwort(roh, REGISTER_IDS);
    if (!geprueft.antwort) {
        return { ok: false, antwort: '', kapitelIds: [], frageId: null, fehler: KEINE_HILFE_ANTWORT };
    }

    const frageId = await merkeFrage(sauber, view, geprueft.antwort, geprueft.kapitelIds);
    return { ok: true, ...geprueft, frageId };
};

/**
 * Daumen hoch/runter zu einer Antwort.
 *
 * Gibt `false` zurueck, wenn nichts geschrieben wurde (keine Id, Tabelle fehlt,
 * RLS). Der Aufrufer darf das ignorieren — die Bewertung ist eine Randnotiz
 * fuer die Kuratierung, kein Nachweis.
 */
export const bewerteAntwort = async (
    frageId: string | null | undefined,
    hilfreich: boolean,
): Promise<boolean> => {
    if (!frageId) return false;
    try {
        const { error } = await supabase
            .from(T_FRAGEN)
            .update({ hilfreich })
            .eq('id', frageId);
        if (error) throw error;
        return true;
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Bewertung nicht gemerkt', e);
        return false;
    }
};
